
import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { SecurityAuditService } from "@/services/securityAuditService";
import { Eye, EyeOff, Copy, Check, AlertTriangle, Shield } from "lucide-react";
import QRCode from "qrcode";
import { TOTP } from "otpauth";
import { MethodSelector } from "./MethodSelector";
import { SMSMethodSetup } from "./SMSMethodSetup";
import { twoFactorService } from "@/services/twoFactorService";

interface TwoFactorAuthDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSettingsChange?: () => void;
}

const TwoFactorAuthDialog = ({ open, onOpenChange, onSettingsChange }: TwoFactorAuthDialogProps) => {
  const { user, profile } = useAuth();
  const [step, setStep] = useState<'setup' | 'verify' | 'manage'>('setup');
  const [selectedMethod, setSelectedMethod] = useState<'totp' | 'sms'>('totp');
  const [currentMethod, setCurrentMethod] = useState<'totp' | 'sms' | null>(null);
  const [secret, setSecret] = useState('');
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [showSecret, setShowSecret] = useState(false);
  const [copiedSecret, setCopiedSecret] = useState(false);
  const [copiedCodes, setCopiedCodes] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [is2FAEnabled, setIs2FAEnabled] = useState(false);
  const [verificationAttempts, setVerificationAttempts] = useState(0);
  const [isRateLimited, setIsRateLimited] = useState(false);
  const [smsPhoneNumber, setSmsPhoneNumber] = useState('');
  const [codesAcknowledged, setCodesAcknowledged] = useState(false);

  // Check if 2FA is already enabled when dialog opens
  useEffect(() => {
    if (open && user) {
      check2FAStatus();
    }
  }, [open, user]);

  const check2FAStatus = async () => {
    if (!user) return;
    
    try {
      const status = await twoFactorService.getStatus(user.id);
      
      if (status && status.enabled) {
        setIs2FAEnabled(true);
        setCurrentMethod(status.method || null);
        setSelectedMethod(status.method || 'totp');
        setStep('manage');
      } else {
        setIs2FAEnabled(false);
        setCurrentMethod(null);
        setStep('setup');
      }
    } catch (error) {
      console.error('Error checking 2FA status:', error);
      setIs2FAEnabled(false);
      setCurrentMethod(null);
      setStep('setup');
    }
  };

  const generateSecret = () => {
    // Generate a cryptographically secure random base32 secret (32 characters minimum)
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    let result = '';
    for (let i = 0; i < 32; i++) {
      result += chars[array[i] % 32];
    }
    
    // Validate secret format
    if (!result || result.length < 16 || !/^[A-Z2-7]+$/.test(result)) {
      toast({
        title: "Error",
        description: "Failed to generate secure secret. Please try again.",
        variant: "destructive"
      });
      return;
    }
    
    setSecret(result);
    
    const serviceName = "Go Dyspo";
    const accountName = profile?.email || user?.email || "User";
    
    const totp = new TOTP({
      issuer: serviceName,
      label: accountName,
      algorithm: "SHA1",
      digits: 6,
      period: 30,
      secret: result,
    });

    const otpAuthUrl = totp.toString();
    
    QRCode.toDataURL(otpAuthUrl)
      .then(setQrCodeUrl)
      .catch(error => {
        console.error('Error generating QR code:', error);
        toast({
          title: "Error",
          description: "Failed to generate QR code. Please try again.",
          variant: "destructive"
        });
      });
  };

  const generateRecoveryCodes = () => {
    // Generate cryptographically secure recovery codes (10 codes, 6 characters each)
    const codes = Array.from({ length: 10 }, () => {
      const array = new Uint8Array(4);
      crypto.getRandomValues(array);
      return Array.from(array)
        .map(b => b.toString(36).toUpperCase())
        .join('')
        .substring(0, 6)
        .padEnd(6, '0');
    });
    setRecoveryCodes(codes);
    return codes;
  };

  const verifyCode = async () => {
    // Check rate limiting
    if (isRateLimited) {
      toast({
        title: "Too many attempts",
        description: "Please wait 15 minutes before trying again.",
        variant: "destructive",
      });
      return;
    }

    // Increment attempt counter
    setVerificationAttempts(prev => prev + 1);

    // Implement client-side rate limiting (5 attempts per 15 minutes)
    if (verificationAttempts >= 4) {
      setIsRateLimited(true);
      setTimeout(() => {
        setIsRateLimited(false);
        setVerificationAttempts(0);
      }, 15 * 60 * 1000); // 15 minutes
      
      toast({
        title: "Too many attempts",
        description: "Maximum verification attempts reached. Please wait 15 minutes.",
        variant: "destructive",
      });
      return;
    }

    // Validate verification code format
    if (!verificationCode || verificationCode.length !== 6 || !/^\d{6}$/.test(verificationCode)) {
      await SecurityAuditService.log2FAVerification(false, 'verify');
      toast({
        title: "Invalid format",
        description: "Verification code must be 6 digits.",
        variant: "destructive",
      });
      return;
    }

    if (!secret) {
      toast({
        title: "Error",
        description: "No secret available. Please try again.",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);
    
    try {
      const totp = new TOTP({
        algorithm: "SHA1",
        digits: 6,
        period: 30,
        secret: secret,
      });

      const isValid = totp.validate({ token: verificationCode, window: 1 });
      
      if (isValid !== null) {
        // Save 2FA settings to database
        const codes = generateRecoveryCodes();
        
        // Hash recovery codes for secure storage
        const { data: hashedCodes, error: hashError } = await supabase.rpc('hash_recovery_codes', {
          codes: codes
        });

        if (hashError) {
          console.error('Error hashing recovery codes:', hashError);
          throw new Error('Failed to secure recovery codes');
        }
        
        const { error } = await supabase
          .from('user_2fa')
          .upsert({
            user_id: user?.id,
            secret: secret,
            enabled: true,
            recovery_codes: codes, // Temporary plaintext for display
            recovery_codes_hash: hashedCodes, // Secure hashed codes
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });

        if (error) throw error;

        // Clear plaintext recovery codes after 5 minutes
        setTimeout(async () => {
          try {
            await supabase
              .from('user_2fa')
              .update({ recovery_codes: null })
              .eq('user_id', user?.id);
            console.log('Plaintext recovery codes cleared after 5 minutes');
          } catch (error) {
            console.error('Error clearing plaintext recovery codes:', error);
          }
        }, 5 * 60 * 1000); // 5 minutes

        setIs2FAEnabled(true);
        setStep('manage');
        
        // Notify parent of settings change
        onSettingsChange?.();
        
        // Log successful 2FA enablement
        await SecurityAuditService.log2FAEnabled();
        await SecurityAuditService.log2FAVerification(true, 'verify');

        // Reset rate limiting on success
        setVerificationAttempts(0);
        setIsRateLimited(false);
        
        toast({
          title: "2FA enabled successfully!",
          description: "Your account is now protected with two-factor authentication.",
        });
      } else {
      // Log failure
        toast({
          title: "Invalid verification code",
          description: "The code you entered is incorrect. Please try again.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error verifying 2FA:', error);
      toast({
        title: "Verification failed",
        description: "There was an error verifying your code. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const regenerateRecoveryCodes = async () => {
    setIsLoading(true);
    setCodesAcknowledged(false);
    
    try {
      console.log('[2FA] Calling regenerate-recovery-codes edge function...');
      
      // Call edge function to regenerate and hash codes server-side
      const { data, error } = await supabase.functions.invoke('regenerate-recovery-codes');

      console.log('[2FA] Edge function response:', { data, error });

      if (error) throw error;

      // Validate response
      if (!data || !Array.isArray(data) || data.length === 0 || data.some(code => !code || code.trim() === '')) {
        console.error('[2FA] Invalid recovery codes received:', data);
        throw new Error('Invalid recovery codes received from server');
      }

      setRecoveryCodes(data);
        
      // Log recovery code regeneration
      await SecurityAuditService.log2FARecoveryCodesRegenerated();
        
      toast({
        title: "Recovery codes regenerated",
        description: "Save these new codes now. They will only be shown once.",
      });
    } catch (error) {
      console.error('[2FA] Error regenerating recovery codes:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to regenerate recovery codes. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const disable2FA = async () => {
    setIsLoading(true);
    
    try {
      const { error } = await supabase
        .from('user_2fa')
        .update({ enabled: false })
        .eq('user_id', user?.id);

      if (error) throw error;

      // Log 2FA disabled event
      await SecurityAuditService.log2FADisabled();

      setIs2FAEnabled(false);
      
      // Notify parent of settings change
      onSettingsChange?.();
      
      toast({
        title: "2FA disabled",
        description: "Two-factor authentication has been disabled for your account.",
      });
      onOpenChange(false);
    } catch (error) {
      console.error('Error disabling 2FA:', error);
      toast({
        title: "Error",
        description: "Failed to disable 2FA. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = async (text: string, type: 'secret' | 'codes') => {
    try {
      // Use secure clipboard API
      if (!navigator.clipboard) {
        throw new Error("Clipboard API not available");
      }
      
      await navigator.clipboard.writeText(text);
      if (type === 'secret') {
        setCopiedSecret(true);
        setTimeout(() => setCopiedSecret(false), 2000);
      } else {
        setCopiedCodes(true);
        setTimeout(() => setCopiedCodes(false), 2000);
      }
      toast({
        title: "Copied!",
        description: type === 'secret' ? "Secret key copied to clipboard" : "Recovery codes copied to clipboard",
      });
    } catch (error) {
      // Fallback for browsers without clipboard API
      toast({
        title: "Copy failed",
        description: "Unable to copy to clipboard. Please copy manually.",
        variant: "destructive"
      });
    }
  };

  const handleSetup = () => {
    if (selectedMethod === 'totp') {
      generateSecret();
      setStep('verify');
    } else {
      // SMS setup will be handled in the SMS setup component
      setStep('verify');
    }
  };

  const handleSMSVerified = async (phoneNumber: string) => {
    // Phone number verified, now generate recovery codes and save
    setSmsPhoneNumber(phoneNumber);
    const codes = generateRecoveryCodes();
    
    try {
      // Hash recovery codes
      const { data: hashedCodes, error: hashError }: any = await (supabase as any).rpc('hash_recovery_codes', {
        codes: codes
      });

      if (hashError) throw new Error('Failed to secure recovery codes');
      
      // Save SMS 2FA settings
      await twoFactorService.setupSMS(user!.id, phoneNumber, hashedCodes);
      
      toast({
        title: "SMS 2FA enabled!",
        description: "Your phone number has been verified and 2FA is now active",
      });
      
      setIs2FAEnabled(true);
      setCurrentMethod('sms');
      setStep('manage');
      
      // Notify parent of settings change
      onSettingsChange?.();
    } catch (error: any) {
      console.error('Error saving SMS 2FA:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to enable SMS 2FA",
        variant: "destructive"
      });
    }
  };

  const handleDialogClose = () => {
    setStep(is2FAEnabled ? 'manage' : 'setup');
    setSelectedMethod('totp');
    setSecret('');
    setQrCodeUrl('');
    setVerificationCode('');
    setRecoveryCodes([]);
    setShowSecret(false);
    setCopiedSecret(false);
    setCopiedCodes(false);
    setSmsPhoneNumber('');
    setCodesAcknowledged(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleDialogClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Two-Factor Authentication
          </DialogTitle>
          <DialogDescription>
            {step === 'setup' && "Add an extra layer of security to your account"}
            {step === 'verify' && "Verify your authenticator app setup"}
            {step === 'manage' && "Manage your 2FA settings"}
          </DialogDescription>
        </DialogHeader>

        {step === 'setup' && (
          <div className="space-y-6">
            <MethodSelector
              value={selectedMethod}
              onChange={setSelectedMethod}
              disabled={isLoading}
            />
            
            <p className="text-sm text-muted-foreground">
              {selectedMethod === 'totp' 
                ? "You'll need an authenticator app (Google Authenticator, Authy, etc.) installed on your mobile device."
                : "You'll need access to your phone to receive text messages."}
            </p>
          </div>
        )}

        {step === 'verify' && selectedMethod === 'sms' && (
          <SMSMethodSetup
            userId={user!.id}
            onVerified={handleSMSVerified}
            onCancel={handleDialogClose}
          />
        )}

        {step === 'verify' && selectedMethod === 'totp' && (
          <div className="space-y-4">
            {recoveryCodes.length > 0 && (
              <Card className="border-godyspo-gold/50 bg-[#1a1f2e]">
                <CardContent className="pt-4">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="h-5 w-5 text-godyspo-gold" />
                    <Label className="text-white font-semibold">⚠️ Save Recovery Codes Now</Label>
                  </div>
                  <p className="text-sm text-gray-300 mb-3 font-medium">
                    You have 10 individual recovery codes below. Each code can only be used ONCE to log in if you lose access to your phone. Save all codes securely - you'll need just ONE code to recover your account.
                  </p>
                  <div className="grid grid-cols-2 gap-2 text-xs font-mono mb-3">
                    {recoveryCodes.map((code, index) => (
                      <div key={index} className="bg-background p-2 rounded border border-godyspo-gold/30">
                        {code}
                      </div>
                    ))}
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="w-full bg-white dark:bg-gray-800"
                    onClick={() => copyToClipboard(recoveryCodes.join('\n'), 'codes')}
                  >
                    {copiedCodes ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
                    Copy All Codes
                  </Button>
                </CardContent>
              </Card>
            )}

            <div className="text-center">
              <p className="text-sm text-muted-foreground mb-4">
                Scan this QR code with your authenticator app:
              </p>
              {qrCodeUrl && (
                <div className="flex justify-center mb-4">
                  <img src={qrCodeUrl} alt="2FA QR Code" className="w-48 h-48" />
                </div>
              )}
            </div>

            <Card>
              <CardContent className="pt-4">
                <Label htmlFor="secret">Or enter this secret key manually:</Label>
                <div className="flex items-center gap-2 mt-2">
                  <Input
                    id="secret"
                    type={showSecret ? "text" : "password"}
                    value={secret}
                    readOnly
                    className="font-mono text-xs"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => setShowSecret(!showSecret)}
                  >
                    {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => copyToClipboard(secret, 'secret')}
                  >
                    {copiedSecret ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              </CardContent>
            </Card>

            <div className="space-y-2">
              <Label htmlFor="verification-code">Enter verification code:</Label>
              <InputOTP
                value={verificationCode}
                onChange={setVerificationCode}
                maxLength={6}
                disabled={isRateLimited}
              >
                <InputOTPGroup>
                  <InputOTPSlot index={0} />
                  <InputOTPSlot index={1} />
                  <InputOTPSlot index={2} />
                  <InputOTPSlot index={3} />
                  <InputOTPSlot index={4} />
                  <InputOTPSlot index={5} />
                </InputOTPGroup>
              </InputOTP>
              {verificationAttempts > 0 && !isRateLimited && (
                <p className="text-sm text-muted-foreground">
                  Attempts remaining: {5 - verificationAttempts}
                </p>
              )}
              {isRateLimited && (
                <p className="text-sm text-destructive">
                  Too many attempts. Please wait 15 minutes.
                </p>
              )}
            </div>
          </div>
        )}

        {step === 'manage' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Two-Factor Authentication</p>
                <p className="text-sm text-muted-foreground">
                  {is2FAEnabled ? "Your account is protected with 2FA" : "2FA is disabled"}
                </p>
              </div>
              <Badge variant={is2FAEnabled ? "default" : "secondary"}>
                {is2FAEnabled ? "Enabled" : "Disabled"}
              </Badge>
            </div>

            {is2FAEnabled && recoveryCodes.length > 0 && (
              <Card className="border-godyspo-gold/50 bg-[#1a1f2e]">
                <CardContent className="pt-4">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="h-5 w-5 text-godyspo-gold" />
                    <Label className="text-white font-semibold">⚠️ Save Recovery Codes Now</Label>
                  </div>
                  <p className="text-sm text-gray-300 mb-3 font-medium">
                    You have 10 individual recovery codes below. Each code can only be used ONCE to log in if you lose access to your phone. Save all codes securely - you'll need just ONE code to recover your account.
                  </p>
                  <div className="grid grid-cols-2 gap-2 text-xs font-mono mb-3">
                    {recoveryCodes.map((code, index) => (
                      <div key={index} className="bg-background p-2 rounded border border-godyspo-gold/30">
                        {code}
                      </div>
                    ))}
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="w-full bg-background mb-3"
                    onClick={() => copyToClipboard(recoveryCodes.join('\n'), 'codes')}
                  >
                    {copiedCodes ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
                    Copy All Codes
                  </Button>
                  <div className="flex items-center space-x-2 pt-2 border-t border-godyspo-gold/30">
                    <Checkbox 
                      id="codes-saved" 
                      checked={codesAcknowledged}
                      onCheckedChange={(checked) => setCodesAcknowledged(checked === true)}
                    />
                    <label 
                      htmlFor="codes-saved" 
                      className="text-sm font-medium text-white cursor-pointer select-none"
                    >
                      I've saved my recovery codes securely
                    </label>
                  </div>
                </CardContent>
              </Card>
            )}

            {is2FAEnabled && recoveryCodes.length === 0 && (
              <Card>
                <CardContent className="pt-4">
                  <p className="text-sm text-muted-foreground mb-3">
                    Lost access to your recovery codes? Generate new ones (this will invalidate your old codes).
                  </p>
                  <Button
                    onClick={regenerateRecoveryCodes}
                    disabled={isLoading}
                    variant="outline"
                    className="w-full"
                  >
                    {isLoading ? "Generating..." : "Regenerate Recovery Codes"}
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        <DialogFooter>
          {step === 'setup' && (
            <>
              <Button variant="outline" onClick={handleDialogClose}>
                Cancel
              </Button>
              <Button onClick={handleSetup} className="bg-godyspo-gold hover:bg-godyspo-gold/90 text-black">
                Set Up 2FA
              </Button>
            </>
          )}

          {step === 'verify' && selectedMethod === 'totp' && (
            <>
              <Button variant="outline" onClick={() => setStep('setup')}>
                Back
              </Button>
              <Button 
                onClick={verifyCode} 
                disabled={isLoading || isRateLimited || verificationCode.length !== 6}
                className="bg-godyspo-gold hover:bg-godyspo-gold/90 text-black"
              >
                {isRateLimited ? "Rate Limited - Wait 15 min" : isLoading ? "Verifying..." : "Verify & Enable"}
              </Button>
            </>
          )}

          {step === 'manage' && (
            <>
              <Button 
                variant="outline" 
                onClick={handleDialogClose}
                disabled={recoveryCodes.length > 0 && !codesAcknowledged}
              >
                Close
              </Button>
              {is2FAEnabled ? (
                <Button 
                  variant="destructive" 
                  onClick={disable2FA}
                  disabled={isLoading}
                >
                  {isLoading ? "Disabling..." : "Disable 2FA"}
                </Button>
              ) : (
                <Button 
                  onClick={handleSetup}
                  className="bg-godyspo-gold hover:bg-godyspo-gold/90 text-black"
                >
                  Enable 2FA
                </Button>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TwoFactorAuthDialog;
